"use client";

import React, { useContext, useEffect, useRef, useState } from "react";
import { useFilter } from "react-aria";
import {
  Button as AriaButton,
  Input as AriaInput,
  ComboBox,
  ComboBoxStateContext,
  Header,
  type InputProps,
  type Key,
  ListBox,
  ListBoxSection,
  ValidationResult,
} from "react-aria-components";
import { useAsyncList } from "react-stately";
import { twMerge } from "tailwind-merge";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useDebounce } from "../../hooks/useDebounce";
import { useDelayedLoading } from "../../hooks/useDelayedLoading";
import { composeTailwindRenderProps } from "../../utils";
import { controlStyles, type Size } from "../../utils/controlStyles";
import { ClearButton, Description, FieldError, getInputBaseStyles, InputWrapper, Label } from "../Field/Field";
import { Icon } from "../Icon/Icon";
import { ListBoxItem } from "../ListBoxItem/ListBoxItem";
import { Loader } from "../Loader/Loader";
import { Popover } from "../Popover/Popover";
import { Tray } from "../Tray/Tray";

interface Item {
  id: string;
  name: string;
}

// Stable empty array reference to prevent useEffect re-runs when staticItems defaults to []
const EMPTY_ITEMS: Item[] = [];

interface Section {
  name: string;
  items: Item[];
}

const iconSizeClassMap = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
  xl: "h-7 w-7",
};

const inputPaddingLeftMap = {
  sm: "pl-8",
  md: "pl-10",
  lg: "pl-11",
  xl: "pl-13",
};

const itemHeightStylesObject = {
  sm: "py-1",
  md: "py-2",
  lg: "py-3",
  xl: "py-4",
};

type RequestMethod = "GET" | "POST";

interface BaseRequestConfig {
  url: string;
  headers?: Record<string, string>;
  transformResponse: (data: unknown) => Item[];
}

interface RestRequestConfig extends BaseRequestConfig {
  requestType: "REST";
  method?: RequestMethod;
  queryKey?: string; // For APIs that expect "query" in URL or JSON body
  extraParams?: Record<string, string>;
  shouldLoad?: (filterText: string) => boolean;
}

interface GraphQLRequestConfig extends BaseRequestConfig {
  requestType: "GraphQL";
  graphqlQuery: string;
  variableKey?: string;
  responsePath?: string;
  shouldLoad?: (filterText: string) => boolean;
}

type AutocompleteRequestConfig = RestRequestConfig | GraphQLRequestConfig;

/**
 * Autocomplete
 *
 * Text input with typeahead suggestions and keyboard navigation.
 */
export interface AutocompleteProps {
  label?: string;
  staticItems?: Item[];
  sections?: Section[];
  selectedKey?: Key | null;
  defaultSelectedKey?: Key | null;
  onSelectionChange?: (key: Key | null) => void;
  requestConfig?: AutocompleteRequestConfig;
  defaultFilter?: (textValue: string, inputValue: string) => boolean;
  placeholder?: string;
  renderItem?: (item: Item) => React.ReactNode;
  renderLeftIcon?: (isLoading: boolean) => React.ReactNode;
  renderSection?: (section: Section, children: React.ReactNode) => React.ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string);
  description?: string;
  size?: Size;
  tooltip?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  validationResult?: ValidationResult;
  showErrors?: boolean;
  autoFocus?: boolean;
  /**
   * Whether to reserve space for error messages to prevent layout shift
   * @default false
   */
  reserveErrorSpace?: boolean;
  /**
   * Whether to hide the dropdown chevron icon
   * @default false
   */
  hideChevron?: boolean;
  /**
   * Whether to use a Tray (bottom sheet) on mobile devices instead of a Popover
   * @default true
   */
  useMobileTray?: boolean;
  /**
   * Additional CSS classes to apply to the component
   */
  className?: string;
}

// Helper function to get item name by key
const getItemName = (key: Key | null | undefined, staticItems: Item[], sections?: Section[]): string => {
  if (key == null || key === "") return "";
  const allItems = sections ? sections.flatMap((s) => s.items) : staticItems || [];
  const selectedItem = allItems.find((item) => item.id === key);
  return selectedItem ? selectedItem.name : "";
};

async function handleRestRequest(
  config: RestRequestConfig,
  filterText: string,
  signal: AbortSignal
): Promise<{ items: Item[]; error?: string }> {
  try {
    // Extract the base URL and access token from config
    const baseUrl = config.url.replace("{q}", filterText);

    // Add query parameters
    const params = new URLSearchParams();
    if (config.extraParams) {
      Object.entries(config.extraParams).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
    }

    // Construct final URL
    const finalUrl = `${baseUrl}${params.toString() ? "?" + params.toString() : ""}`;

    const response = await fetch(finalUrl, {
      method: "GET",
      headers: config.headers,
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    return { items: config.transformResponse(json) };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return { items: [] };
    }
    console.error("Error in REST request:", error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    return { items: [], error: errorMessage };
  }
}

async function handleGraphQLRequest(
  config: GraphQLRequestConfig,
  filterText: string,
  signal: AbortSignal
): Promise<{ items: Item[]; error?: string }> {
  try {
    const query = config.graphqlQuery.replace(`$${config.variableKey || "filter"}`, `"${filterText}"`);

    const response = await fetch(config.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...config.headers,
      },
      body: JSON.stringify({ query }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let json = await response.json();

    if (json.errors) {
      throw new Error(json.errors[0]?.message || "GraphQL error occurred");
    }

    // For GraphQL, traverse the response path if specified
    if (config.responsePath) {
      json = config.responsePath.split(".").reduce((obj, key) => obj?.[key], json);
    }

    return { items: config.transformResponse(json) };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return { items: [] };
    }
    console.error("Error in GraphQL request:", error);
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    return { items: [], error: errorMessage };
  }
}

// React Aria opens a focus-triggered ComboBox only on focus, so a click on the already-focused input reopens it here.
// The first click after focus selects the text, as keyboard focus already does, so typing replaces the selection. It is
// deferred because Chrome collapses a selection that was still there from before the click once the click has finished.
function ComboBoxInput({
  reopenOnClick,
  selectOnFocus,
  onFocus,
  onClick,
  ...props
}: InputProps & { reopenOnClick: boolean; selectOnFocus: boolean }) {
  const state = useContext(ComboBoxStateContext);
  const isFirstClick = useRef(false);
  return (
    <AriaInput
      {...props}
      onFocus={(event) => {
        onFocus?.(event);
        isFirstClick.current = true;
      }}
      onClick={(event) => {
        onClick?.(event);
        const input = event.currentTarget;
        if (selectOnFocus && isFirstClick.current) setTimeout(() => input.select(), 0);
        isFirstClick.current = false;
        if (reopenOnClick && state && !state.isOpen) state.open(null, "manual");
      }}
    />
  );
}

export function Autocomplete({
  label,
  staticItems = EMPTY_ITEMS,
  sections,
  selectedKey,
  defaultSelectedKey,
  onSelectionChange,
  requestConfig,
  defaultFilter,
  placeholder,
  errorMessage,
  description,
  size = "md",
  tooltip,
  isRequired,
  isDisabled,
  isInvalid,
  validationResult,
  showErrors = true,
  renderItem,
  renderLeftIcon,
  renderSection,
  autoFocus = false,
  reserveErrorSpace = false,
  hideChevron = false,
  useMobileTray = true,
  className,
}: AutocompleteProps) {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const trayInputRef = React.useRef<HTMLInputElement>(null);
  const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>();
  const [uncontrolledSelectedKey, setUncontrolledSelectedKey] = useState<Key | null>(defaultSelectedKey ?? null);
  const currentSelectedKey = selectedKey === undefined ? uncontrolledSelectedKey : selectedKey;
  const [inputValue, setInputValue] = useState(() => getItemName(currentSelectedKey, staticItems, sections));
  const [pickCount, setPickCount] = useState(0);
  const syncedRef = useRef({ key: currentSelectedKey, name: inputValue, pickCount });
  const [isFocused, setIsFocused] = useState(false);
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const [trayQuery, setTrayQuery] = useState("");
  const { contains } = useFilter({ sensitivity: "base" });
  const [error, setError] = useState<string | undefined>();
  const { isMobile } = useBreakpoint();
  const shouldUseTray = useMobileTray && isMobile;
  const searchText = shouldUseTray ? trayQuery : inputValue;
  const debouncedSearchText = useDebounce(searchText);

  const textSizeClass = controlStyles[size].text;

  // Always call useAsyncList unconditionally (React hook rules)
  const dynamicItemsHook = useAsyncList<Item>({
    async load({ signal, filterText = "" }) {
      if (!requestConfig) {
        return { items: [] };
      }
      if (!requestConfig.url || (requestConfig.shouldLoad && !requestConfig.shouldLoad(filterText))) {
        return { items: [] };
      }

      try {
        let result;
        if (requestConfig.requestType === "GraphQL") {
          result = await handleGraphQLRequest(requestConfig, filterText, signal);
        } else {
          result = await handleRestRequest(requestConfig, filterText, signal);
        }

        setError(result.error);
        return { items: result.items };
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return { items: [] };
        }
        console.error("Error loading items:", error);
        setError(error instanceof Error ? error.message : "Unknown error occurred");
        return { items: [] };
      }
    },
    initialFilterText: "",
  });

  // Only use dynamicItems if requestConfig exists and the search is not empty
  const dynamicItems = requestConfig && searchText !== "" && debouncedSearchText !== "" ? dynamicItemsHook : null;
  const loadedItems = dynamicItemsHook.items;
  const nameOf = (key: Key | null | undefined) =>
    getItemName(key, staticItems, sections) || getItemName(key, loadedItems);
  let isLoadingDelayed = false;

  // Track last filter text to prevent duplicate setFilterText calls
  const lastFilterTextRef = useRef<string>("");

  useEffect(() => {
    if (autoFocus) {
      setIsFocused(true);
    }
  }, [autoFocus]);

  // Overwrite inputValue when the selection changes or after a pick the parent may not have applied. A newly resolved
  // name (items arriving late) only fills a field that isn't being edited and never blanks one, so typing survives.
  useEffect(() => {
    const synced = syncedRef.current;
    // An unchanged key missing from the current results keeps its last known name.
    const newName = nameOf(currentSelectedKey) || (currentSelectedKey === synced.key ? synced.name : "");
    const selectionChanged = currentSelectedKey !== synced.key || pickCount !== synced.pickCount;
    const nameResolved = newName !== "" && newName !== synced.name && inputValue === synced.name;
    if (!selectionChanged && !nameResolved) return;
    syncedRef.current = { key: currentSelectedKey, name: newName, pickCount };
    setInputValue(newName);
  }, [currentSelectedKey, staticItems, sections, loadedItems, pickCount, inputValue]);

  // Update trigger width when component mounts or resizes
  useEffect(() => {
    const updateTriggerWidth = () => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    };

    updateTriggerWidth();
    window.addEventListener("resize", updateTriggerWidth);
    return () => window.removeEventListener("resize", updateTriggerWidth);
  }, []);

  useEffect(() => {
    if (debouncedSearchText === "") {
      // Reset ref when input is cleared so future searches for the same term work
      lastFilterTextRef.current = "";
    } else if (dynamicItems && debouncedSearchText !== lastFilterTextRef.current) {
      lastFilterTextRef.current = debouncedSearchText;
      dynamicItems.setFilterText(debouncedSearchText);
    }
  }, [debouncedSearchText, dynamicItems]);

  // Use delayed loading to prevent loader flash for fast requests
  isLoadingDelayed = useDelayedLoading(!!dynamicItems?.isLoading, 300);

  const filteredTrayItems = React.useMemo(() => {
    const itemsToFilter = dynamicItems ? dynamicItems.items : staticItems;
    if (!shouldUseTray || !trayQuery) {
      return itemsToFilter;
    }

    const filterFn = defaultFilter ?? (requestConfig ? () => true : contains);
    return itemsToFilter.filter((item) => filterFn(item.name, trayQuery));
  }, [shouldUseTray, trayQuery, dynamicItems, staticItems, defaultFilter, requestConfig, contains]);

  const handleInternalSelectionChange = (key: Key | null) => {
    setUncontrolledSelectedKey(key);
    setInputValue(nameOf(key));
    setPickCount((count) => count + 1);
    if (onSelectionChange && key !== currentSelectedKey) {
      onSelectionChange(key);
    }
  };

  const handleClear = () => {
    setUncontrolledSelectedKey(null);
    setInputValue("");
    onSelectionChange?.(null);
  };

  const handleInternalInputChange = (text: string) => {
    setInputValue(text);
    // Don't clear selection while user is typing - let the ComboBox handle it
    // The selection will be cleared naturally by react-aria when no match is found
  };

  // On mobile with tray, open the tray instead of dropdown
  const handleInputPress = () => {
    if (shouldUseTray) {
      setTrayQuery("");
      setIsTrayOpen(true);
    }
  };

  return (
    <>
      <ComboBox
        // In tray mode the ComboBox renders no list, so its focus/blur commits report a null selection; picks come from the tray.
        onSelectionChange={shouldUseTray ? undefined : handleInternalSelectionChange}
        selectedKey={selectedKey}
        defaultSelectedKey={defaultSelectedKey || undefined}
        defaultItems={sections ? sections.flatMap((section) => section.items) : staticItems}
        items={dynamicItems ? dynamicItems.items : undefined}
        className={composeTailwindRenderProps(className, "group flex flex-col w-full")}
        menuTrigger={shouldUseTray ? "manual" : requestConfig ? "input" : "focus"}
        allowsEmptyCollection={!!requestConfig}
        isDisabled={isDisabled}
        isInvalid={isInvalid || !!errorMessage}
        inputValue={inputValue}
        onInputChange={shouldUseTray ? undefined : handleInternalInputChange}
        autoFocus={autoFocus && !shouldUseTray}
        defaultFilter={defaultFilter ?? (requestConfig ? () => true : contains)}
        aria-label={label || placeholder || "Autocomplete"}
      >
        {(state) => (
          <div className="flex flex-col">
            {label && (
              <Label size={size} tooltip={tooltip} isRequired={isRequired}>
                {label}
              </Label>
            )}
            <InputWrapper ref={triggerRef}>
              {renderLeftIcon ? (
                <div className="pointer-events-none absolute left-3 top-[45%] -translate-y-1/2 z-10">
                  {renderLeftIcon(isLoadingDelayed)}
                </div>
              ) : (
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 flex items-center">
                  {isLoadingDelayed ? (
                    <Loader size={16} color="text-text-secondary" />
                  ) : (
                    <Icon name="MagnifyingGlass" className={`${iconSizeClassMap[size]} text-text-secondary`} />
                  )}
                </div>
              )}
              <ComboBoxInput
                reopenOnClick={!shouldUseTray && !requestConfig}
                selectOnFocus={!shouldUseTray}
                className={getInputBaseStyles({
                  isInvalid: isInvalid || !!errorMessage,
                  isDisabled,
                  isFocused: isFocused || (shouldUseTray ? isTrayOpen : state.isOpen),
                  size,
                  className: twMerge(
                    inputPaddingLeftMap[size],
                    // Add right padding for clear button or chevron
                    (inputValue && !isDisabled) || (!hideChevron && !inputValue) ? "pr-10" : "pr-3",
                    shouldUseTray && "cursor-pointer",
                    className
                  ),
                })}
                placeholder={placeholder}
                disabled={isDisabled}
                onFocus={(e) => {
                  setIsFocused(true);
                  if (shouldUseTray) {
                    e.currentTarget.blur();
                    handleInputPress();
                  }
                }}
                onBlur={() => {
                  setIsFocused(false);
                }}
                onClick={shouldUseTray ? handleInputPress : undefined}
                readOnly={shouldUseTray}
                aria-label={label || placeholder || "Autocomplete"}
              />
              {inputValue && !isDisabled && <ClearButton onClick={handleClear} size={size} />}
              {!hideChevron && !inputValue && (
                <AriaButton
                  className="absolute right-3 top-[46%] -translate-y-1/2 transform text-text-secondary hover:text-text-primary"
                  aria-label="Toggle menu"
                  isDisabled={isDisabled}
                >
                  {({ isPressed }) => <Icon name={isPressed ? "CaretUp" : "CaretDown"} className="h-4 w-4" />}
                </AriaButton>
              )}
            </InputWrapper>
            {reserveErrorSpace ? (
              <div className="mt-1 min-h-[20px]">
                {showErrors && error ? (
                  <FieldError size={size} className="text-feedback-error-text">
                    {error}
                  </FieldError>
                ) : showErrors && typeof errorMessage === "string" ? (
                  <FieldError size={size} className="text-feedback-error-text">
                    {errorMessage}
                  </FieldError>
                ) : showErrors && typeof errorMessage === "function" && validationResult ? (
                  <FieldError size={size} className="text-feedback-error-text">
                    {errorMessage(validationResult)}
                  </FieldError>
                ) : description ? (
                  <Description size={size}>{description}</Description>
                ) : null}
              </div>
            ) : (
              <>
                {description && (
                  <Description size={size} className="mt-1">
                    {description}
                  </Description>
                )}
                {showErrors && error && (
                  <FieldError size={size} className="text-feedback-error-text mt-1">
                    {error}
                  </FieldError>
                )}
                {showErrors && typeof errorMessage === "string" && (
                  <FieldError size={size} className="text-feedback-error-text mt-1">
                    {errorMessage}
                  </FieldError>
                )}
                {showErrors && typeof errorMessage === "function" && validationResult && (
                  <FieldError size={size} className="text-feedback-error-text mt-1">
                    {errorMessage(validationResult)}
                  </FieldError>
                )}
              </>
            )}
            {!shouldUseTray && (
              <Popover
                className="bg-background-input shadow-xl border border-border-input/20 backdrop-blur-sm ring-1 ring-black/5 rounded-[var(--control-border-radius)]"
                style={{
                  minWidth: triggerWidth ? `${triggerWidth}px` : undefined,
                  zIndex: 9999,
                }}
              >
                <ListBox
                  aria-label={label || placeholder || "Suggestions"}
                  className={`max-h-[300px] overflow-auto bg-background-input outline-none ${textSizeClass}`}
                >
                  {sections
                    ? sections.map((section) => {
                        const sectionItems = section.items.map((item) => (
                          <ListBoxItem
                            key={item.id}
                            id={item.id}
                            textValue={item.name}
                            size={size}
                            className={`flex cursor-default items-center gap-2 px-3 ${itemHeightStylesObject[size]} text-text-primary outline-none data-[focused]:bg-background-hover data-[selected]:bg-background-selected data-[selected]:font-medium data-[focus-visible]:ring-0`}
                          >
                            {renderItem ? renderItem(item) : item.name}
                          </ListBoxItem>
                        ));

                        if (renderSection) {
                          const content = renderSection(section, sectionItems);
                          return React.isValidElement(content) && content.type === ListBoxSection ? (
                            content
                          ) : (
                            <ListBoxSection key={section.name}>{content}</ListBoxSection>
                          );
                        }

                        return (
                          <ListBoxSection key={section.name}>
                            <Header className="px-3 py-1 text-sm font-bold text-text-secondary">{section.name}</Header>
                            {sectionItems}
                          </ListBoxSection>
                        );
                      })
                    : (item: Item) => (
                        <ListBoxItem
                          key={item.id}
                          id={item.id}
                          textValue={item.name}
                          size={size}
                          className={`flex cursor-default items-center gap-2 px-3 ${itemHeightStylesObject[size]} text-text-primary outline-none data-[focused]:bg-background-hover data-[selected]:bg-background-selected data-[selected]:font-medium data-[focus-visible]:ring-0`}
                        >
                          {renderItem ? renderItem(item) : item.name}
                        </ListBoxItem>
                      )}
                </ListBox>
              </Popover>
            )}
          </div>
        )}
      </ComboBox>
      {shouldUseTray && (
        <Tray
          isOpen={isTrayOpen}
          onClose={() => setIsTrayOpen(false)}
          title={label ?? placeholder ?? "Search"}
          contentPadding={false}
          height="80vh"
        >
          <div className="flex flex-col h-full">
            {/* Search input at the top */}
            <div className="p-4 border-b border-border-default">
              <InputWrapper>
                <div className="pointer-events-none absolute left-3 top-[45%] -translate-y-1/2 z-10 flex items-center">
                  {isLoadingDelayed ? (
                    <Loader size={16} color="text-text-secondary" />
                  ) : (
                    <Icon name="MagnifyingGlass" className="h-4 w-4 text-text-secondary" />
                  )}
                </div>
                <input
                  ref={trayInputRef}
                  type="text"
                  value={trayQuery}
                  onChange={(e) => setTrayQuery(e.target.value)}
                  placeholder={placeholder}
                  autoFocus
                  className={getInputBaseStyles({
                    isInvalid: false,
                    isDisabled: false,
                    isFocused: true,
                    size: "md",
                    className: "pl-10 pr-10",
                  })}
                />
                {trayQuery && <ClearButton onClick={() => setTrayQuery("")} size="md" />}
              </InputWrapper>
            </div>

            {/* Results list */}
            <div className="flex-1 overflow-auto">
              <ListBox
                aria-label={label || placeholder || "Suggestions"}
                items={filteredTrayItems}
                selectedKeys={currentSelectedKey != null ? [currentSelectedKey] : []}
                selectionMode="single"
                onSelectionChange={(keys) => {
                  const key = Array.from(keys)[0];
                  if (key) {
                    handleInternalSelectionChange(key);
                    setIsTrayOpen(false);
                  }
                }}
                className="bg-background-input outline-none"
              >
                {(item: Item) => (
                  <ListBoxItem
                    key={item.id}
                    id={item.id}
                    textValue={item.name}
                    size="md"
                    className="flex cursor-default items-center gap-2 px-4 py-3 text-text-primary outline-none data-[focused]:bg-background-hover data-[selected]:bg-background-selected data-[selected]:font-medium data-[focus-visible]:ring-0"
                  >
                    {renderItem ? renderItem(item) : item.name}
                  </ListBoxItem>
                )}
              </ListBox>
            </div>
          </div>
        </Tray>
      )}
    </>
  );
}
